import React, { useMemo, useState } from 'react';
import { FilePenLine, FileText, Loader2 } from 'lucide-react';
import {
  PORTAL_AKTE_SCHREIBEN_TEMPLATES,
  buildPortalAkteSchreibenText,
  getPortalAkteSchreibenTemplate,
  portalAkteSchreibenDisplayName,
  validatePortalAkteSchreibenDraft,
  type PortalAkteSchreibenFormat,
  type PortalAkteSchreibenTemplateId,
} from './portalAkteSchreiben';

export type PortalAkteSchreibenExportResult = {
  ok: boolean;
  message: string;
};

type PortalAkteSchreibenPanelProps = {
  akteId: string;
  akteTitle: string;
  kanzleiName?: string;
  canWrite: boolean;
  docxAvailable: boolean;
  pdfAvailable: boolean;
  onExport: (
    format: PortalAkteSchreibenFormat,
    draft: { text: string; displayName: string },
  ) => Promise<PortalAkteSchreibenExportResult>;
  onShowDocuments: () => void;
};

const FIELD_CLASS =
  'w-full rounded-lg border border-white/[0.1] bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-violet-400/60';

export const PortalAkteSchreibenPanel: React.FC<PortalAkteSchreibenPanelProps> = ({
  akteId,
  akteTitle,
  kanzleiName,
  canWrite,
  docxAvailable,
  pdfAvailable,
  onExport,
  onShowDocuments,
}) => {
  const [templateId, setTemplateId] =
    useState<PortalAkteSchreibenTemplateId>('kanzleischreiben');
  const [recipient, setRecipient] = useState('');
  const [address, setAddress] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busyFormat, setBusyFormat] = useState<PortalAkteSchreibenFormat | null>(null);
  const [notice, setNotice] = useState<PortalAkteSchreibenExportResult | null>(null);

  const draftInput = useMemo(
    () => ({
      akteId,
      templateId,
      recipient,
      address,
      subject,
      body,
      kanzleiName,
    }),
    [address, akteId, body, kanzleiName, recipient, subject, templateId],
  );
  const selectedTemplate = getPortalAkteSchreibenTemplate(templateId);

  const exportDraft = async (format: PortalAkteSchreibenFormat) => {
    setNotice(null);
    if (!canWrite) {
      setNotice({ ok: false, message: 'Für diese Akte besteht nur Leserecht.' });
      return;
    }
    const validation = validatePortalAkteSchreibenDraft(draftInput);
    if (validation.ok === false) {
      setNotice({ ok: false, message: validation.error });
      return;
    }
    if ((format === 'docx' && !docxAvailable) || (format === 'pdf' && !pdfAvailable)) {
      setNotice({
        ok: false,
        message: `${format.toUpperCase()} ist in diesem Portal-Build nicht vollständig angebunden.`,
      });
      return;
    }

    setBusyFormat(format);
    try {
      const result = await onExport(format, {
        text: buildPortalAkteSchreibenText(draftInput),
        displayName: portalAkteSchreibenDisplayName(draftInput, format),
      });
      setNotice(result);
    } catch (error) {
      setNotice({
        ok: false,
        message:
          error instanceof Error
            ? error.message
            : 'Schreiben konnte nicht in der Akte gespeichert werden.',
      });
    } finally {
      setBusyFormat(null);
    }
  };

  return (
    <section
      data-testid="portal-akte-schreiben"
      data-akte-id={akteId}
      className="space-y-4 rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-4"
    >
      <div>
        <p className="text-sm font-semibold text-slate-100 m-0">Schreiben aus Vorlage</p>
        <p className="mt-1 text-[11px] text-slate-400">
          Ziel: Akte „{akteTitle}“. Kein ARKO- oder Composer-Schritt nötig; ARKO bleibt optional.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Schreiben-Vorlage">
        {PORTAL_AKTE_SCHREIBEN_TEMPLATES.map((template) => {
          const active = template.id === templateId;
          return (
            <button
              key={template.id}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`portal-schreiben-template-${template.id}`}
              onClick={() => {
                setTemplateId(template.id);
                setNotice(null);
              }}
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                active
                  ? 'border-violet-400/60 bg-violet-500/15 text-violet-100'
                  : 'border-white/[0.08] bg-slate-950/40 text-slate-300 hover:border-white/[0.16]'
              }`}
            >
              <span className="block text-xs font-semibold">{template.label}</span>
              <span className="mt-1 block text-[10px] text-slate-500">{template.hint}</span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-[11px] text-slate-400">
          <span>Empfänger *</span>
          <input
            value={recipient}
            onChange={(event) => setRecipient(event.target.value)}
            className={FIELD_CLASS}
            placeholder="Name / Organisation"
            data-testid="portal-schreiben-recipient"
          />
        </label>
        <label className="space-y-1 text-[11px] text-slate-400">
          <span>Anschrift (optional)</span>
          <input
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            className={FIELD_CLASS}
            placeholder="Straße, PLZ Ort"
          />
        </label>
      </div>

      <label className="block space-y-1 text-[11px] text-slate-400">
        <span>Betreff *</span>
        <input
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          className={FIELD_CLASS}
          placeholder={selectedTemplate.label}
          data-testid="portal-schreiben-subject"
        />
      </label>

      <label className="block space-y-1 text-[11px] text-slate-400">
        <span>Inhalt * — wird nicht automatisch juristisch ergänzt</span>
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          className={`${FIELD_CLASS} min-h-36 resize-y`}
          placeholder="Inhalt des Schreibens selbst formulieren …"
          data-testid="portal-schreiben-body"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={Boolean(busyFormat) || !canWrite || !docxAvailable}
          onClick={() => void exportDraft('docx')}
          data-testid="portal-schreiben-export-docx"
          className="inline-flex items-center gap-1.5 rounded-lg border border-blue-500/40 bg-blue-600/15 px-3 py-2 text-xs font-semibold text-blue-200 hover:bg-blue-600/25 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busyFormat === 'docx' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FilePenLine className="h-3.5 w-3.5" />
          )}
          DOCX in diese Akte
        </button>
        <button
          type="button"
          disabled={Boolean(busyFormat) || !canWrite || !pdfAvailable}
          onClick={() => void exportDraft('pdf')}
          data-testid="portal-schreiben-export-pdf"
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/35 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-200 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busyFormat === 'pdf' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileText className="h-3.5 w-3.5" />
          )}
          PDF in diese Akte
        </button>
      </div>

      {notice ? (
        <div
          role={notice.ok ? 'status' : 'alert'}
          data-testid="portal-schreiben-result"
          className={`rounded-lg border px-3 py-2 text-xs ${
            notice.ok
              ? 'border-emerald-500/25 bg-emerald-500/[0.08] text-emerald-200'
              : 'border-red-500/30 bg-red-500/[0.08] text-red-200'
          }`}
        >
          <span>{notice.message}</span>
          {notice.ok ? (
            <button
              type="button"
              onClick={onShowDocuments}
              className="ml-2 font-semibold underline underline-offset-2 hover:text-white"
            >
              Im Tab Dokumente anzeigen
            </button>
          ) : null}
        </div>
      ) : null}

      <p className="text-[10px] leading-relaxed text-slate-500 m-0">
        Entwurfsgerüst, keine Rechtsberatung und keine Zusage zur rechtlichen Richtigkeit.
        Inhalt, Empfänger und Betreff bleiben in Ihrer Verantwortung.
      </p>
    </section>
  );
};
